import { Connection, Edge, Node } from 'reactflow';
import { processorArrayFc } from '../arrayFunctionts';
import { allNodesTargetBasedOnType } from '../dataStoredToType';
import { createSystemTimestamp } from '../dateUtil';
import { deepEqual } from '../deepEqualData';
import { validState } from '../validateNodeState';
import type { FlowTriggerAutosaveData } from './flowTriggerAutosave.types';
import {
  collectOutgoingTargetIds,
  propagateDownstreamFromSeeds,
} from './propagateDownstreamFromSeeds';
import {
  flowTriggerDataHandle,
  getOutputByType,
  givingDataToNode,
} from './utilis';

export type { FlowTriggerAutosaveData } from './flowTriggerAutosave.types';

interface FluxBoxConnectionItem {
  id: string;
  text: string;
  label: string;
  handleId: string;
}

interface FluxBoxDataObjects {
  textData: { [key: string]: FluxBoxConnectionItem[] };
  fileLinkData: { [key: string]: FluxBoxConnectionItem[] };
}

const addFluxBoxConnectionData = ({
  textData,
  fileLinkData,
  targetHandle,
  sourceId,
  text,
  label,
}: {
  textData: { [key: string]: FluxBoxConnectionItem[] };
  fileLinkData: { [key: string]: FluxBoxConnectionItem[] };
  targetHandle: string;
  sourceId: string;
  text: string;
  label: string;
}): FluxBoxDataObjects => {
  const newTextData = { ...textData };
  const newFileLinkData = { ...fileLinkData };

  if (targetHandle?.endsWith('Text')) {
    const varInputNodeId = targetHandle.replace('Text', '');
    const newItem: FluxBoxConnectionItem = {
      id: sourceId,
      text: text || '',
      label: label || '',
      handleId: targetHandle,
    };
    const currentItems = Array.isArray(newTextData[varInputNodeId])
      ? newTextData[varInputNodeId]
      : [];
    const existingIndex = currentItems.findIndex(item => item.id === sourceId);

    if (existingIndex >= 0) {
      currentItems[existingIndex] = {
        ...currentItems[existingIndex],
        text: newItem.text,
        label: newItem.label,
        handleId: newItem.handleId,
      };
      newTextData[varInputNodeId] = currentItems;
    } else {
      newTextData[varInputNodeId] = [...currentItems, newItem];
    }
  } else if (targetHandle?.endsWith('FileLink')) {
    const varInputNodeId = targetHandle.replace('FileLink', '');
    const newItem: FluxBoxConnectionItem = {
      id: sourceId,
      text: text || '',
      label: label || '',
      handleId: targetHandle,
    };
    const currentItems = Array.isArray(newFileLinkData[varInputNodeId])
      ? newFileLinkData[varInputNodeId]
      : [];
    const existingIndex = currentItems.findIndex(item => item.id === sourceId);

    if (existingIndex >= 0) {
      currentItems[existingIndex] = {
        ...currentItems[existingIndex],
        text: newItem.text,
        label: newItem.label,
        handleId: newItem.handleId,
      };
      newFileLinkData[varInputNodeId] = currentItems;
    } else {
      newFileLinkData[varInputNodeId] = [...currentItems, newItem];
    }
  }

  return { textData: newTextData, fileLinkData: newFileLinkData };
};

const removeFluxBoxConnectionData = ({
  textData,
  fileLinkData,
  targetHandle,
  sourceId,
}: {
  textData: { [key: string]: FluxBoxConnectionItem[] };
  fileLinkData: { [key: string]: FluxBoxConnectionItem[] };
  targetHandle: string;
  sourceId: string;
}): FluxBoxDataObjects & { clearedHandle: string } => {
  let newTextData = { ...textData };
  let newFileLinkData = { ...fileLinkData };
  let clearedHandle = '';

  if (targetHandle?.endsWith('Text')) {
    const varInputNodeId = targetHandle.replace('Text', '');
    const currentItems = Array.isArray(newTextData[varInputNodeId])
      ? newTextData[varInputNodeId]
      : [];
    const filteredItems = currentItems.filter(item => item.id !== sourceId);

    if (filteredItems.length > 0) {
      newTextData[varInputNodeId] = filteredItems;
    } else {
      const { [varInputNodeId]: _, ...restData } = newTextData;
      newTextData = restData;
    }
    clearedHandle = targetHandle;
  } else if (targetHandle?.endsWith('FileLink')) {
    const varInputNodeId = targetHandle.replace('FileLink', '');
    const currentItems = Array.isArray(newFileLinkData[varInputNodeId])
      ? newFileLinkData[varInputNodeId]
      : [];
    const filteredItems = currentItems.filter(item => item.id !== sourceId);

    if (filteredItems.length > 0) {
      newFileLinkData[varInputNodeId] = filteredItems;
    } else {
      const { [varInputNodeId]: _, ...restData } = newFileLinkData;
      newFileLinkData = restData;
    }
    clearedHandle = targetHandle;
  }

  return {
    textData: newTextData,
    fileLinkData: newFileLinkData,
    clearedHandle,
  };
};

const updateNodeData = ({
  node,
  id,
  objectData,
}: {
  node: Node[];
  id: string;
  objectData: {
    [key: string]: string;
  };
}) => {
  return node.map(nd => {
    if (nd.id === id) {
      return {
        ...nd,
        data: {
          ...nd.data,
          ...objectData,
          ...validState(nd?.data?.isEmptyState || false),
        },
      };
    }

    if (objectData?.run === 'Run from here' && nd.id !== id) {
      return {
        ...nd,
        data: {
          ...nd.data,
          run: null,
        },
      };
    }

    return nd;
  });
};

const changeLabel = ({
  node,
  id,
  objectData,
  edges,
  objectCallerData,
}: {
  node: Node<any>[];
  id: string;
  objectData: {
    [key: string]: string;
  };
  edges: Edge[];
  objectCallerData: {
    id: string;
    text: {
      id: string;
      text: string;
    }[];
  }[];
}) => {
  const findAllEdges = edges?.filter(edge => edge.source === id);

  const combinedData: {
    [target: string]: { [targetHandle: string]: any };
  } = {};

  if (findAllEdges?.length) {
    findAllEdges.forEach((edge: any) => {
      const target = edge.target;
      const targetHandle = edge.targetHandle;

      if (!combinedData[target]) {
        combinedData[target] = {};
      }

      if (!combinedData?.[target][targetHandle]) {
        combinedData[target][targetHandle] = {};
      }

      getOutputByType({
        edge,
        nds: node,
        allEdges: edges,
        combinedData,
        objectCallerData,
        combinedTexts: {},
      });
    });
  }

  return node.map(nd => {
    const combinedTextsKeys = Object.keys(combinedData);

    const ndTypeAsKeyOfAllNodesTargetBasedOnType =
      allNodesTargetBasedOnType[
        nd.type as keyof typeof allNodesTargetBasedOnType
      ];

    if (nd.id === id) {
      return {
        ...nd,
        data: {
          ...nd.data,
          label: objectData.label,
        },
      };
    }

    if (
      combinedTextsKeys?.includes(nd?.id) &&
      ndTypeAsKeyOfAllNodesTargetBasedOnType
    ) {
      const targetKeys = Object?.keys(combinedData[nd.id]);

      const getValuesFrom = Object?.keys(
        ndTypeAsKeyOfAllNodesTargetBasedOnType,
      );

      const getAlsoValues = Object?.values(
        ndTypeAsKeyOfAllNodesTargetBasedOnType,
      );

      const keyMapping: any = getValuesFrom?.reduce((acc: any, key, index) => {
        acc[key] = getAlsoValues?.[index];
        return acc;
      }, {});

      const newObject: {
        [key: string]: any;
      } = {};

      targetKeys.forEach((key: string) => {
        const mappedKey = keyMapping[key];

        if (mappedKey) {
          newObject[mappedKey + 'Data'] = combinedData[nd.id][key];
        }
      });

      const newObjectKeys = Object.keys(newObject);

      let newData: {
        [key: string]: string;
      } = {};

      newObjectKeys.forEach((key: string) => {
        if (key) {
          const findData = nd?.data?.[key];

          if (!newData[key]) {
            newData[key] = findData;
          }

          if (findData?.length) {
            const mappedData = findData.map(
              (item: { id: string; text: string }) => {
                if (newObject[key]?.id === item?.id) {
                  return {
                    ...item,
                    label: objectData.label,
                  };
                }
                return item;
              },
            );

            newData[key] = mappedData;
          }
        }
      });

      return {
        ...nd,
        data: {
          ...nd.data,
          ...newData,
        },
      };
    }

    return nd;
  });
};

const updateEdgeData = ({
  updateEdge,
  node,
  objectCallerData,
  edges,
}: {
  updateEdge: {
    oldEdge: Edge;
    newConnection: Connection | Edge;
  };
  node: Node[];
  edges?: Edge[];
  objectCallerData?:
    | {
        id: string;
        text: {
          id: string;
          text: string;
        }[];
      }[]
    | undefined;
}) => {
  let isSourceHandleChanged = false;

  if (updateEdge?.oldEdge?.source && updateEdge?.newConnection?.source) {
    const { oldEdge, newConnection } = updateEdge;

    isSourceHandleChanged =
      oldEdge.source === newConnection.source &&
      oldEdge.sourceHandle !== newConnection.sourceHandle &&
      oldEdge.target === newConnection.target &&
      oldEdge.targetHandle === newConnection.targetHandle;

    const oldEdgeData = givingDataToNode({
      edge: oldEdge,
      nds: node,
      type: 'delete',
      objectCallerData,
      isSourceHandleChanged,
      edges,
    });

    const newConnectionData = givingDataToNode({
      edge: newConnection as Edge,
      nds: node,
      type: 'add',
      objectCallerData,
      isSourceHandleChanged,
      edges,
    });

    if (oldEdgeData.skipHandleDataWrite && newConnectionData.skipHandleDataWrite) {
      return node;
    }

    return node?.map((nd: any) => {
      if (updateEdge?.oldEdge?.source && updateEdge?.newConnection?.source) {
        if (
          (nd.type === 'textBox' ||
            nd.type === 'outputObjectNode' ||
            nd.type === 'richTextBox' ||
            nd.type === 'outputController') &&
          nd.id === oldEdge.target &&
          nd.id === newConnection.target &&
          oldEdge.targetHandle === newConnection.targetHandle
        ) {
          const textData = nd.data?.textData || [];

          let oldSourceId = oldEdge.source;
          if (
            oldEdge?.source?.includes('nodesBox') ||
            oldEdge?.source?.includes('fluxBox') ||
            oldEdge?.source?.includes('libraryNode') ||
            oldEdge?.source?.includes('scripting') ||
            (oldEdge?.source?.includes('commandTextNode') &&
              oldEdge?.sourceHandle === 'fileLink') ||
            (oldEdge?.source?.includes('commandTextNode') &&
              oldEdge?.sourceHandle === 'output-memory') ||
            (oldEdge?.source?.includes('commandTextNode') &&
              oldEdge?.sourceHandle === 'logs') ||
            (oldEdge?.source?.includes('varInputNode') &&
              oldEdge?.sourceHandle === 'outputExtractText')
          ) {
            oldSourceId = oldEdge.source + '$' + oldEdge.sourceHandle;
          }

          const filteredTextData = textData.filter(
            (item: any) => item?.id !== oldSourceId,
          );

          const newSourceId =
            newConnectionData?.data?.id || newConnection.source;

          const existingIndex = filteredTextData.findIndex(
            (item: any) => item?.id === newSourceId,
          );

          let updatedTextData;
          if (existingIndex >= 0) {
            updatedTextData = [...filteredTextData];
            updatedTextData[existingIndex] = {
              ...updatedTextData[existingIndex],
              text: newConnectionData?.data?.text || '',
              label: newConnectionData?.data?.label || '',
            };
          } else {
            updatedTextData = [
              ...filteredTextData,
              {
                id: newSourceId,
                text: newConnectionData?.data?.text || '',
                label: newConnectionData?.data?.label || '',
              },
            ];
          }

          return {
            ...nd,
            data: {
              ...nd.data,
              textData: updatedTextData,
            },
          };
        }
      }

      if (nd?.id === newConnection.target && nd?.id !== oldEdge.target) {
        if (nd.type === 'fluxBox' || nd.type === 'libraryNode') {
          const { textData, fileLinkData } = addFluxBoxConnectionData({
            textData: nd.data?.textData ? { ...nd.data.textData } : {},
            fileLinkData: nd.data?.fileLinkData
              ? { ...nd.data.fileLinkData }
              : {},
            targetHandle: newConnection.targetHandle || '',
            sourceId: newConnection.source || '',
            text: newConnectionData?.data?.text || '',
            label: newConnectionData?.data?.label || '',
          });

          return {
            ...nd,
            data: {
              ...nd.data,
              textData,
              fileLinkData,
            },
          };
        }

        if (
          newConnection.targetHandle ===
            nodePassOnlyTextRealHandleId[
              nd?.type as keyof typeof nodePassOnlyTextRealHandleId
            ] ||
          (nd?.type === 'mathFunction' &&
            newConnection.targetHandle !== 'input' &&
            newConnection.targetHandle !== 'formula') ||
          nd?.type === 'nodesBox' ||
          nd?.type === 'fileSave' ||
          (nd?.type === 'scripting' &&
            newConnection.targetHandle !== 'input' &&
            newConnection.targetHandle !== 'script')
        ) {
          let data = {};

          if (nd.type === 'mathFunction' || nd.type === 'scripting') {
            const variables = nd.data?.variables || [];

            const newVariables = variables.map((item: any) => {
              if (item.id === newConnection.targetHandle) {
                return {
                  ...item,
                  value: newConnectionData?.data?.text,
                };
              }
              return item;
            });

            data = {
              variables: newVariables,
            };
          }

          if (nd.type === 'fileSave') {
            const files = nd.data?.files || [];

            const newFiles = files.map((item: any) => {
              const isName = newConnection.targetHandle?.includes('-name');
              const isFile = newConnection.targetHandle?.includes('-file');

              if (
                item.id === newConnection.targetHandle?.replace('-file', '') ||
                item.id === newConnection.targetHandle?.replace('-name', '')
              ) {
                return {
                  ...item,
                  value: isFile ? newConnectionData?.data?.text : item.value,
                  fileName: isName
                    ? newConnectionData?.data?.text
                    : item.fileName,
                };
              }
              return item;
            });

            data = {
              files: newFiles,
            };
          }

          if (
            nd.type === 'arrayNode' &&
            newConnection.targetHandle === 'content'
          ) {
            const formData = {
              options: 'Paragraph',
              processorArray: [],
              customOperator: '',
              startingPoint: 1,
              endingPoint: 5,
              completed: 1,
              executingArray: 1,
              executing: 'default',
              auto: false,
              breakWords: '',
              preInput: '',
              postInput: '',
              processorArrayItemLength: 1,
              ...nd?.data,
              content: newConnectionData?.data?.text,
            };

            const processorArray = processorArrayFc(formData);

            data = {
              processorArray,
              content: newConnectionData?.data?.text,
            };
          }

          const nodePassOnlyTextKey =
            nodePassOnlyText[nd.type as keyof typeof nodePassOnlyText];

          return {
            ...nd,
            data: {
              ...nd.data,
              ...(nodePassOnlyTextKey
                ? { [nodePassOnlyTextKey]: newConnectionData.data.text }
                : {}),
              ...data,
            },
          };
        } else {
          if (
            !newConnectionData.checkIfAlreadyConnected &&
            !newConnectionData.skipHandleDataWrite
          ) {
            const newData = [
              ...newConnectionData.handleData,
              newConnectionData.data,
            ];

            return {
              ...nd,
              data: {
                ...nd.data,
                [newConnectionData.getType + 'Data']: newData,
              },
            };
          }
        }
      }

      if (nd.id === oldEdge.target && nd.id !== newConnection.target) {
        if (nd.type === 'fluxBox' || nd.type === 'libraryNode') {
          const { textData, fileLinkData, clearedHandle } =
            removeFluxBoxConnectionData({
              textData: nd.data?.textData ? { ...nd.data.textData } : {},
              fileLinkData: nd.data?.fileLinkData
                ? { ...nd.data.fileLinkData }
                : {},
              targetHandle: oldEdge.targetHandle || '',
              sourceId: oldEdge.source || '',
            });

          return {
            ...nd,
            data: {
              ...nd.data,
              textData,
              fileLinkData,
              ...(clearedHandle ? { [clearedHandle]: '' } : {}),
            },
          };
        }

        if (
          oldEdge.targetHandle ===
            nodePassOnlyTextRealHandleId[
              nd?.type as keyof typeof nodePassOnlyTextRealHandleId
            ] ||
          (nd?.type === 'mathFunction' &&
            oldEdge.targetHandle !== 'input' &&
            oldEdge.targetHandle !== 'formula') ||
          nd?.type === 'nodesBox' ||
          nd?.type === 'fileSave' ||
          (nd?.type === 'scripting' &&
            oldEdge.targetHandle !== 'input' &&
            oldEdge.targetHandle !== 'script')
        ) {
          let data = {};

          if (nd.type === 'mathFunction' || nd.type === 'scripting') {
            const variables = nd.data?.variables || [];

            const newVariables = variables.map((item: any) => {
              if (item.id === oldEdge.targetHandle) {
                return {
                  ...item,
                  value: '',
                };
              }
              return item;
            });

            data = {
              variables: newVariables,
            };
          }

          if (nd.type === 'fileSave') {
            const files = nd.data?.files || [];

            const newFiles = files.map((item: any) => {
              const isName = oldEdge.targetHandle?.includes('-name');
              const isFile = oldEdge.targetHandle?.includes('-file');

              if (
                item.id === oldEdge.targetHandle?.replace('-file', '') ||
                item.id === oldEdge.targetHandle?.replace('-name', '')
              ) {
                return {
                  ...item,
                  value: isFile ? '' : item.value,
                  fileName: isName ? '' : item.fileName,
                };
              }
              return item;
            });

            data = {
              files: newFiles,
            };
          }

          if (nd.type === 'nodesBox') {
            data = {
              [String(oldEdge.targetHandle)]: '',
            };
          }

          if (nd.type === 'arrayNode') {
            data = {
              processorArray: [],
              content: '',
            };
          }

          const nodePassOnlyTextKeyForOld =
            nodePassOnlyText[nd.type as keyof typeof nodePassOnlyText];

          return {
            ...nd,
            data: {
              ...nd.data,
              ...(nodePassOnlyTextKeyForOld
                ? { [nodePassOnlyTextKeyForOld]: '' }
                : {}),
              ...data,
            },
          };
        } else if (!oldEdgeData.skipHandleDataWrite) {
          return {
            ...nd,
            data: {
              ...nd.data,
              [oldEdgeData.getType + 'Data']: oldEdgeData.handleData,
            },
          };
        }
      }

      if (nd?.id === oldEdge.target && nd?.id === newConnection.target) {
        let object: any = {};

        if (nd.type === 'fluxBox' || nd.type === 'libraryNode') {
          const removeResult = removeFluxBoxConnectionData({
            textData: nd.data?.textData ? { ...nd.data.textData } : {},
            fileLinkData: nd.data?.fileLinkData
              ? { ...nd.data.fileLinkData }
              : {},
            targetHandle: oldEdge.targetHandle || '',
            sourceId: oldEdge.source || '',
          });

          const addResult = addFluxBoxConnectionData({
            textData: removeResult.textData,
            fileLinkData: removeResult.fileLinkData,
            targetHandle: newConnection.targetHandle || '',
            sourceId: newConnection.source || '',
            text: newConnectionData?.data?.text || '',
            label: newConnectionData?.data?.label || '',
          });

          return {
            ...nd,
            data: {
              ...nd.data,
              textData: addResult.textData,
              fileLinkData: addResult.fileLinkData,
              ...(removeResult.clearedHandle
                ? { [removeResult.clearedHandle]: '' }
                : {}),
            },
          };
        }

        if (
          oldEdge.targetHandle ===
            nodePassOnlyTextRealHandleId[
              nd?.type as keyof typeof nodePassOnlyTextRealHandleId
            ] ||
          (nd?.type === 'mathFunction' &&
            oldEdge.targetHandle !== 'input' &&
            oldEdge.targetHandle !== 'formula') ||
          (nd?.type === 'scripting' &&
            oldEdge.targetHandle !== 'input' &&
            oldEdge.targetHandle !== 'script') ||
          nd?.type === 'nodesBox' ||
          nd?.type === 'fileSave'
        ) {
          if (nd.type === 'mathFunction' || nd.type === 'scripting') {
            const variables = nd.data?.variables || [];

            const newVariables = variables.map((item: any) => {
              if (item.id === oldEdge.targetHandle) {
                return {
                  ...item,
                  value: '',
                };
              }
              return item;
            });

            object = {
              variables: newVariables,
            };
          }

          if (nd.type === 'fileSave') {
            const files = nd.data?.files || [];

            const newFiles = files.map((item: any) => {
              const isName = oldEdge.targetHandle?.includes('-name');
              const isFile = oldEdge.targetHandle?.includes('-file');

              if (
                item.id === oldEdge.targetHandle?.replace('-file', '') ||
                item.id === oldEdge.targetHandle?.replace('-name', '')
              ) {
                return {
                  ...item,
                  value: isFile ? '' : item.value,
                  fileName: isName ? '' : item.fileName,
                };
              }

              return item;
            });

            object = {
              files: newFiles,
            };
          }

          if (nd.type === 'nodesBox') {
            object = {
              [String(oldEdge.targetHandle)]: '',
            };
          }

          if (nd.type === 'arrayNode') {
            object = {
              processorArray: [],
              content: '',
              ...object,
            };
          }

          const nodePassOnlyTextKeyObj =
            nodePassOnlyText[nd.type as keyof typeof nodePassOnlyText];

          if (nodePassOnlyTextKeyObj) {
            object = {
              [nodePassOnlyTextKeyObj]: '',
              ...object,
            };
          }
        }

        if (
          newConnection.targetHandle ===
            nodePassOnlyTextRealHandleId[
              nd?.type as keyof typeof nodePassOnlyTextRealHandleId
            ] ||
          (nd?.type === 'mathFunction' &&
            newConnection.targetHandle !== 'input' &&
            newConnection.targetHandle !== 'formula') ||
          (nd?.type === 'scripting' &&
            newConnection.targetHandle !== 'input' &&
            newConnection.targetHandle !== 'script') ||
          nd?.type === 'nodesBox' ||
          nd?.type === 'fileSave'
        ) {
          if (nd.type === 'mathFunction' || nd.type === 'scripting') {
            const variables = object?.variables
              ? object?.variables
              : nd.data?.variables || [];
            const newVariables = variables.map((item: any) => {
              if (item.id === newConnection.targetHandle) {
                return {
                  ...item,
                  value: newConnectionData?.data?.text,
                };
              }
              return item;
            });

            object = {
              ...object,
              variables: newVariables,
            };
          }

          if (nd.type === 'fileSave') {
            const files = object?.files ? object?.files : nd.data?.files || [];
            const newfiles = files.map((item: any) => {
              const isName = newConnection.targetHandle?.includes('-name');
              const isFile = newConnection.targetHandle?.includes('-file');

              if (
                item.id === newConnection.targetHandle?.replace('-file', '') ||
                item.id === newConnection.targetHandle?.replace('-name', '')
              ) {
                return {
                  ...item,
                  value: isFile ? newConnectionData?.data?.text : item.value,
                  fileName: isName
                    ? newConnectionData?.data?.text
                    : item.fileName,
                };
              }
              return item;
            });

            object = {
              ...object,
              files: newfiles,
            };
          }

          if (nd.type === 'nodesBox') {
            object = {
              ...object,
              [String(newConnection.targetHandle)]:
                newConnectionData?.data?.text,
            };
          }

          if (nd.type === 'arrayNode') {
            const formData = {
              options: 'Paragraph',
              processorArray: [],
              customOperator: '',
              startingPoint: 1,
              endingPoint: 5,
              completed: 1,
              executingArray: 1,
              executing: 'default',
              auto: false,
              breakWords: '',
              preInput: '',
              postInput: '',
              processorArrayItemLength: 1,
              ...nd?.data,
              content: newConnectionData?.data?.text,
            };

            const processorArray = processorArrayFc(formData);

            object = {
              ...object,
              processorArray,
              content: newConnectionData?.data?.text,
            };
          }

          object = {
            ...object,
          };
        }

        const newData = isSourceHandleChanged
          ? [newConnectionData.data]
          : [...newConnectionData.handleData, newConnectionData.data];

        if (
          newConnectionData.checkIfAlreadyConnected &&
          !newConnectionData.skipHandleDataWrite
        ) {
          const newTargetHandleData = newConnectionData.handleData.map(item => {
            if (item.id === oldEdge.source) {
              return {
                ...item,
                text: newConnectionData?.data?.text,
                label: newConnectionData?.data?.label,
              };
            }
            return item;
          });

          object = {
            ...object,
            [newConnectionData.getType + 'Data']: newTargetHandleData,
          };
        }

        if (nd.type === 'mathFunction' || nd.type === 'scripting') {
          object = {
            ...object,
            variables: object?.variables || nd?.data?.variables,
          };
        }

        if (nd.type === 'fileSave') {
          object = {
            ...object,
            files: object?.files || nd?.data?.files,
          };
        }

        if (isSourceHandleChanged) {
          return {
            ...nd,
            data: {
              ...nd.data,
              ...(newConnectionData.skipHandleDataWrite
                ? {}
                : { [newConnectionData.getType + 'Data']: newData }),
              ...object,
            },
          };
        } else {
          return {
            ...nd,
            data: {
              ...nd.data,
              ...(oldEdgeData.skipHandleDataWrite
                ? {}
                : {
                    [oldEdgeData.getType + 'Data']: oldEdgeData.handleData,
                  }),
              ...(newConnectionData.skipHandleDataWrite
                ? {}
                : { [newConnectionData.getType + 'Data']: newData }),
              ...object,
            },
          };
        }
      }

      return nd;
    });
  }

  return node;
};

const connect = ({
  connectData,
  node,
  objectCallerData,
  edges,
}: {
  connectData: Edge;
  node: Node[];
  edges?: Edge[];
  objectCallerData?:
    | {
        id: string;
        text: {
          id: string;
          text: string;
        }[];
      }[]
    | undefined;
}) => {
  const { data: connectedText, findTarget } = givingDataToNode({
    edge: connectData,
    nds: node,
    type: 'add',
    objectCallerData,
    edges,
  });

  if (
    (findTarget?.type === 'fluxBox' || findTarget?.type === 'libraryNode') &&
    (connectData.targetHandle?.endsWith('Text') ||
      connectData.targetHandle?.endsWith('FileLink'))
  ) {
    const targetNode = node.find(nd => nd.id === connectData.target);

    const { textData, fileLinkData } = addFluxBoxConnectionData({
      textData: targetNode?.data?.textData
        ? { ...targetNode.data.textData }
        : {},
      fileLinkData: targetNode?.data?.fileLinkData
        ? { ...targetNode.data.fileLinkData }
        : {},
      targetHandle: connectData.targetHandle || '',
      sourceId: connectData.source || '',
      text: connectedText?.text || '',
      label: connectedText?.label || '',
    });

    return node.map(nd => {
      if (nd.id === connectData.target) {
        return {
          ...nd,
          data: {
            ...nd.data,
            textData,
            fileLinkData,
          },
        };
      }
      return nd;
    });
  }

  if (
    connectData.targetHandle ===
      nodePassOnlyTextRealHandleId[
        findTarget?.type as keyof typeof nodePassOnlyTextRealHandleId
      ] ||
    (findTarget?.type === 'mathFunction' &&
      connectData.targetHandle !== 'input' &&
      connectData.targetHandle !== 'formula') ||
    (findTarget?.type === 'scripting' &&
      connectData.targetHandle !== 'input' &&
      connectData.targetHandle !== 'script') ||
    findTarget?.type === 'nodesBox' ||
    findTarget?.type === 'fileSave'
  ) {
    return node.map(nd => {
      if (nd.id === connectData?.target) {
        let data = {};

        if (nd.type === 'mathFunction' || nd.type === 'scripting') {
          const variables: {
            id: string;
            value: string;
          }[] = nd.data?.variables || [];

          const newVariables = variables.map(item => {
            if (item.id === connectData.targetHandle) {
              return {
                ...item,
                value: connectedText.text,
              };
            }
            return item;
          });

          data = {
            variables: newVariables,
          };
        }

        if (nd.type === 'fileSave') {
          const files: {
            id: string;
            value: string;
            fileName: string;
          }[] = nd.data?.files || [];

          const newfiles = files.map(item => {
            const isName = connectData.targetHandle?.includes('-name');
            const isFile = connectData.targetHandle?.includes('-file');

            if (
              item.id === connectData.targetHandle?.replace('-file', '') ||
              item.id === connectData.targetHandle?.replace('-name', '')
            ) {
              return {
                ...item,
                value: isFile ? connectedText.text : item.value,
                fileName: isName ? connectedText.text : item.fileName,
              };
            }
            return item;
          });

          data = {
            files: newfiles,
          };
        }

        if (nd.type === 'arrayNode') {
          const formData = {
            options: 'Paragraph',
            processorArray: [],
            customOperator: '',
            startingPoint: 1,
            endingPoint: 5,
            completed: 1,
            executingArray: 1,
            executing: 'default',
            auto: false,
            breakWords: '',
            preInput: '',
            postInput: '',
            processorArrayItemLength: 1,
            ...nd?.data,
            content: connectedText.text,
          };

          const processorArray = processorArrayFc(formData);

          data = {
            processorArray,
            content: connectedText.text,
          };
        }

        if (nd.type === 'nodesBox') {
          data = {
            [String(connectData?.targetHandle)]: connectedText.text,
          };
        }

        const nodePassOnlyTextKeyConnect =
          nodePassOnlyText[nd.type as keyof typeof nodePassOnlyText];

        return {
          ...nd,
          data: {
            ...nd.data,
            ...(nodePassOnlyTextKeyConnect
              ? { [nodePassOnlyTextKeyConnect]: connectedText.text }
              : {}),
            ...data,
          },
        };
      }
      return nd;
    });
  } else {
    const {
      checkIfAlreadyConnected,
      handleData: targetHandleData,
      getType,
      data,
      skipHandleDataWrite,
    } = givingDataToNode({
      edge: connectData,
      nds: node,
      type: 'add',
      objectCallerData,
      edges,
    });

    if (skipHandleDataWrite) {
      return node;
    }

    if (!checkIfAlreadyConnected) {
      const newTargetHandleData = [...targetHandleData, data];
      const findTargetNode = node.find(nd => nd.id === connectData?.target);

      return node?.map(nd => {
        if (nd.id === connectData?.target) {
          return {
            ...nd,
            data: {
              ...nd.data,
              [getType + 'Data']: newTargetHandleData,
              ...(findTargetNode?.type === 'thirdPartyIntegration'
                ? { [getType]: data?.text ?? '' }
                : {}),
            },
          };
        }
        return nd;
      });
    }
  }
  return node;
};

const deleteEdgesData = ({
  deleteEdges,
  node,
  objectCallerData,
  edges,
}: {
  deleteEdges: Edge[];
  node: Node[];
  edges?: Edge[];
  objectCallerData?:
    | {
        id: string;
        text: {
          id: string;
          text: string;
        }[];
      }[]
    | undefined;
}) => {
  let newNodes = [...node];

  if (deleteEdges?.length) {
    deleteEdges.forEach((edgeToDelete: Edge) => {
      const {
        checkIfAlreadyConnected,
        handleData: targetHandleData,
        getType,
        findTarget,
        skipHandleDataWrite,
      } = givingDataToNode({
        edge: edgeToDelete,
        nds: node,
        type: 'delete',
        objectCallerData,
        edges,
      });

      if (skipHandleDataWrite) {
        return;
      }

      if (
        findTarget?.type === 'thirdPartyIntegration' &&
        edgeToDelete.targetHandle?.startsWith('field_')
      ) {
        const fieldName = edgeToDelete.targetHandle.slice(6);
        newNodes = newNodes.map(nd => {
          if (nd.id === edgeToDelete.target) {
            return {
              ...nd,
              data: {
                ...nd.data,
                [fieldName]: '',
                [fieldName + 'Data']: targetHandleData ?? [],
              },
            };
          }
          return nd;
        });
      } else if (
        (findTarget?.type === 'fluxBox' ||
          findTarget?.type === 'libraryNode') &&
        (edgeToDelete.targetHandle?.endsWith('Text') ||
          edgeToDelete.targetHandle?.endsWith('FileLink'))
      ) {
        newNodes = newNodes.map(nd => {
          if (nd.id === edgeToDelete.target) {
            const { textData, fileLinkData, clearedHandle } =
              removeFluxBoxConnectionData({
                textData: nd.data?.textData ? { ...nd.data.textData } : {},
                fileLinkData: nd.data?.fileLinkData
                  ? { ...nd.data.fileLinkData }
                  : {},
                targetHandle: edgeToDelete.targetHandle || '',
                sourceId: edgeToDelete.source || '',
              });

            return {
              ...nd,
              data: {
                ...nd.data,
                textData,
                fileLinkData,
                ...(clearedHandle ? { [clearedHandle]: '' } : {}),
              },
            };
          }
          return nd;
        });
      } else if (
        edgeToDelete.targetHandle ===
          nodePassOnlyTextRealHandleId[
            findTarget?.type as keyof typeof nodePassOnlyTextRealHandleId
          ] ||
        (findTarget?.type === 'mathFunction' &&
          edgeToDelete.targetHandle !== 'input' &&
          edgeToDelete.targetHandle !== 'formula') ||
        findTarget?.type === 'nodesBox' ||
        findTarget?.type === 'fileSave' ||
        (findTarget?.type === 'scripting' &&
          edgeToDelete.targetHandle !== 'input' &&
          edgeToDelete.targetHandle !== 'script')
      ) {
        newNodes = node.map(nd => {
          if (nd.id === edgeToDelete?.target) {
            let data = {};

            let targetHandle = edgeToDelete.targetHandle;
            if (nd.type === 'mathFunction' || nd.type === 'scripting') {
              const variables: {
                id: string;
                value: string;
              }[] = nd.data?.variables || [];

              const newVariables = variables.map(item => {
                if (item.id === edgeToDelete.targetHandle) {
                  return {
                    ...item,
                    value: '',
                  };
                }
                return item;
              });

              data = {
                variables: newVariables,
              };
            }

            if (nd.type === 'fileSave') {
              const files: {
                id: string;
                fileName: string;
                value: string;
              }[] = nd.data?.files || [];

              const newfiles = files.map(item => {
                const isFile = edgeToDelete.targetHandle?.includes('-file');
                const isName = edgeToDelete.targetHandle?.includes('-name');

                if (
                  item.id === edgeToDelete.targetHandle?.replace('-file', '') ||
                  item.id === edgeToDelete.targetHandle?.replace('-name', '')
                ) {
                  return {
                    ...item,
                    value: isFile ? '' : item.value,
                    fileName: isName ? '' : item.fileName,
                  };
                }
                return item;
              });

              data = {
                files: newfiles,
              };
            }

            if (nd.type === 'arrayNode') {
              data = {
                processorArray: [],
                content: '',
              };
            }

            if (nd.type === 'nodesBox') {
              data = {
                [String(targetHandle)]: '',
              };
            }

            const nodePassOnlyTextKeyDelete =
              nodePassOnlyText[
                findTarget?.type as keyof typeof nodePassOnlyText
              ];
            const shouldClearBlobConfig =
              nodePassOnlyTextKeyDelete &&
              !(
                nd.type === 'thirdPartyIntegration' &&
                nodePassOnlyTextKeyDelete === 'blobConfig'
              );

            return {
              ...nd,
              data: {
                ...nd.data,
                ...(shouldClearBlobConfig
                  ? { [nodePassOnlyTextKeyDelete!]: '' }
                  : {}),
                ...data,
              },
            };
          }
          return nd;
        });
      } else {
        newNodes = newNodes.map(nd => {
          if (nd.id === edgeToDelete.target) {
            return {
              ...nd,
              data: {
                ...nd.data,
                [getType + 'Data']: targetHandleData,
              },
            };
          }
          return nd;
        });
      }
    });
  }

  return newNodes;
};

const defaultTest = ({
  flowTriggerData,
  node,
  id,
  objectData,
  edges,
  changeType,
  objectCallerData,
}: {
  flowTriggerData?: FlowTriggerAutosaveData;
  node: Node[];
  id: string;
  objectData: {
    [key: string]: string;
  };
  edges: Edge[];
  changeType: string;
  objectCallerData?:
    | {
        id: string;
        text: {
          id: string;
          text: string;
        }[];
      }[]
    | undefined;
}) => {
  let updatedNodes = modifyData(
    node,
    id,
    objectData,
    edges,
    changeType,
    objectCallerData,
  );

  const visited = new Set<string>([id]);
  let currentTargets = edges.filter(e => e.source === id).map(e => e.target);
  while (currentTargets.length > 0) {
    const nextTargets: string[] = [];
    for (const targetId of currentTargets) {
      if (visited.has(targetId)) continue;
      visited.add(targetId);
      const hasOutgoing = edges.some(e => e.source === targetId);
      if (!hasOutgoing) continue;
      updatedNodes = modifyData(updatedNodes, targetId, {}, edges, changeType, objectCallerData);
      edges.filter(e => e.source === targetId).forEach(e => nextTargets.push(e.target));
    }
    currentTargets = nextTargets;
  }

  const newNodes = flowTriggerDataHandle({
    nds: updatedNodes,
    flowTriggerData,
    edges,
    id,
  });

  let update = modifyData(
    newNodes,
    flowTriggerData?.arrayExecute?.id || '',
    {},
    edges,
    changeType,
    objectCallerData,
  );

  if (changeType === 'execute') {
    const seedSet = new Set<string>();
    for (const t of collectOutgoingTargetIds(edges, id)) {
      seedSet.add(t);
    }
    const arrayExecId = flowTriggerData?.arrayExecute?.id;
    if (arrayExecId) {
      for (const t of collectOutgoingTargetIds(edges, arrayExecId)) {
        seedSet.add(t);
      }
    }
    update = propagateDownstreamFromSeeds(
      update,
      edges,
      [...seedSet],
      modifyData,
      objectCallerData,
    );
  } else {
    edges
      ?.map(edg => edg.source)
      ?.forEach((id: string) => {
        const findNode = update.find(nd => nd.id === id);

        if (findNode) {
          update = modifyData(
            update,
            id,
            findNode.data,
            edges,
            changeType,
            objectCallerData,
          );
        }
      });
  }

  return update;
};

const collectAffectedIdsForDeleteEdgesSnapshot = (
  deleteEdges: Edge[],
  edges: Edge[],
): Set<string> => {
  const ids = new Set<string>();
  for (const e of deleteEdges) {
    if (e.source) {
      ids.add(e.source);
    }
    if (e.target) {
      ids.add(e.target);
    }
  }
  const queue = deleteEdges
    .map(e => e.target)
    .filter((t): t is string => Boolean(t));
  const seen = new Set<string>();
  while (queue.length > 0) {
    const u = queue.shift();
    if (!u || seen.has(u)) {
      continue;
    }
    seen.add(u);
    ids.add(u);
    for (const t of collectOutgoingTargetIds(edges, u)) {
      queue.push(t);
    }
  }
  return ids;
};

const buildDataByIdBefore = (
  node: Node[],
  changeType: string,
  deleteEdges: Edge[] | undefined,
  edges: Edge[],
): Map<string, Record<string, unknown>> => {
  const map = new Map<string, Record<string, unknown>>();
  if (changeType === 'delete-edges' && deleteEdges?.length) {
    const affectedIds = collectAffectedIdsForDeleteEdgesSnapshot(
      deleteEdges,
      edges,
    );
    for (const n of node) {
      if (!n?.id) {
        continue;
      }
      if (affectedIds.has(n.id)) {
        map.set(n.id, structuredClone(n.data) as Record<string, unknown>);
      } else {
        map.set(n.id, n.data as Record<string, unknown>);
      }
    }
    return map;
  }
  for (const n of node) {
    if (!n?.id) {
      continue;
    }
    map.set(n.id, structuredClone(n.data) as Record<string, unknown>);
  }
  return map;
};

const handleChanges = ({
  node,
  changeType,
  id,
  objectData,
  edges,
  updateEdge,
  connectData,
  deleteEdges,
  flowTriggerData,
  objectCallerData,
}: {
  node: Node[];
  changeType: string;
  id: string;
  objectData: {
    [key: string]: string;
  };
  edges: Edge[];
  updateEdge?: {
    oldEdge: Edge;
    newConnection: Connection | Edge;
  };
  connectData?: Edge;
  deleteEdges?: Edge[];
  flowTriggerData?: FlowTriggerAutosaveData;
  objectCallerData?:
    | {
        id: string;
        text: {
          id: string;
          text: string;
        }[];
      }[]
    | undefined;
}): { nds: Node[]; node: Node[]; dataChangedNodeIds: string[] } => {
  const dataByIdBefore = buildDataByIdBefore(
    node,
    changeType,
    deleteEdges,
    edges,
  );
  const objectCallerRows = objectCallerData ?? [];
  let newNds = node;
  if (changeType === 'default' || changeType === 'changeText') {
    const updatedNode = updateNodeData({ node, id, objectData });
    const hasOutgoingEdges = edges.some(edge => edge.source === id);
    if (hasOutgoingEdges) {
      newNds = modifyData(
        updatedNode,
        id,
        objectData,
        edges,
        changeType,
        objectCallerData,
      );
      newNds = propagateDownstreamFromSeeds(
        newNds,
        edges,
        collectOutgoingTargetIds(edges, id),
        modifyData,
        objectCallerData,
      );
    } else {
      newNds = updatedNode;
    }
  } else if (changeType === 'changeLabel') {
    newNds = changeLabel({
      node,
      id,
      objectData,
      edges,
      objectCallerData: objectCallerRows,
    });
  } else if (changeType === 'update-edge') {
    if (updateEdge) {
      newNds = updateEdgeData({
        updateEdge,
        node,
        objectCallerData: objectCallerRows,
        edges,
      });
    }
  } else if (changeType === 'connect') {
    if (connectData) {
      newNds = connect({
        connectData,
        node,
        objectCallerData: objectCallerRows,
        edges,
      });
      if (connectData.target) {
        newNds = propagateDownstreamFromSeeds(
          newNds,
          edges,
          [
            connectData.target,
            ...collectOutgoingTargetIds(edges, connectData.target),
          ],
          modifyData,
          objectCallerData,
        );
      }
    }
  } else if (changeType === 'delete-edges') {
    const edgesToDelete = deleteEdges ?? [];
    newNds = deleteEdgesData({
      deleteEdges: edgesToDelete,
      node,
      objectCallerData,
      edges,
    });
    const deletedTargets = [
      ...new Set(
        edgesToDelete.map(e => e.target).filter((t): t is string => Boolean(t)),
      ),
    ];
    newNds = propagateDownstreamFromSeeds(
      newNds,
      edges,
      deletedTargets,
      modifyData,
      objectCallerData,
    );
  } else {
    newNds = defaultTest({
      flowTriggerData,
      node,
      id,
      objectData,
      edges,
      changeType,
      objectCallerData,
    });
  }

  const dataChangedNodeIds: string[] = [];
  for (const n of newNds) {
    if (!n?.id) {
      continue;
    }
    const before = dataByIdBefore.get(n.id);
    if (before === undefined) {
      continue;
    }
    if (!deepEqual(before, n.data as Record<string, unknown>)) {
      dataChangedNodeIds.push(n.id);
    }
  }

  return { nds: newNds, node, dataChangedNodeIds };
};

export const modifyData = (
  node: Node[],
  id: string,
  objectData: {
    [key: string]: string;
  },
  edges: Edge[],
  changeType?: string,
  objectCallerData?: {
    id: string;
    text: {
      id: string;
      text: string;
    }[];
  }[],
) => {
  const combinedTexts: {
    [target: string]: { [targetHandle: string]: string };
  } = {};

  const combinedData: {
    [target: string]: { [targetHandle: string]: any };
  } = {};

  let nds = node.map(nd => {
    if (nd.id === id) {
      return {
        ...nd,
        data: {
          ...nd.data,
          ...objectData,
        },
      };
    }

    return nd;
  });

  const filteredEdges =
    edges?.filter(
      edg => edg?.target === id || edg?.source === id || edg?.source === id,
    ) ?? [];

  const edgeGroups = new Map<string, Edge[]>();
  for (const edge of filteredEdges) {
    const th = edge.targetHandle ?? '';
    const k = `${edge.target}::${th}`;
    let group = edgeGroups.get(k);
    if (!group) {
      group = [];
      edgeGroups.set(k, group);
    }
    group.push(edge);
  }

  for (const groupEdges of edgeGroups.values()) {
    const edge0 = groupEdges[0];
    const target = edge0.target;
    const targetHandle = edge0.targetHandle!;

    if (!combinedData[target]) {
      combinedData[target] = {};
    }

    if (groupEdges.length === 1) {
      if (!combinedData[target][targetHandle]) {
        combinedData[target][targetHandle] = {};
      }
      getOutputByType({
        edge: edge0,
        nds,
        allEdges: edges,
        combinedData,
        combinedTexts,
        objectCallerData,
      });
    } else {
      const pieces: { label?: string; id?: string; text?: string }[] = [];
      for (const edge of groupEdges) {
        const localCombined: {
          [key: string]: {
            [key: string]: { label?: string; id?: string; text?: string };
          };
        } = {
          [target]: { [targetHandle]: {} },
        };
        const localTexts: {
          [key: string]: { [key: string]: string };
        } = {
          [target]: { [targetHandle]: '' },
        };
        getOutputByType({
          edge,
          nds,
          allEdges: edges,
          combinedData: localCombined,
          combinedTexts: localTexts,
          objectCallerData,
        });
        const piece = localCombined[target]?.[targetHandle];
        if (
          piece &&
          typeof piece === 'object' &&
          !Array.isArray(piece) &&
          piece.id !== undefined
        ) {
          pieces.push(piece);
        }
      }
      combinedData[target][targetHandle] =
        pieces as unknown as (typeof combinedData)[string][string];
    }
  }

  return (nds = nds?.map(nd => {
    const combinedTextsKeys = Object.keys(combinedData);

    const ndTypeAsKeyOfAllNodesTargetBasedOnType =
      allNodesTargetBasedOnType[
        nd.type as keyof typeof allNodesTargetBasedOnType
      ];

    const ndType: any = nd.type;

    const check: any =
      ndType !== 'mathFunction' ||
      ndType !== 'nodesBox' ||
      ndType !== 'fluxBox' ||
      ndType !== 'libraryNode' ||
      ndType !== 'fluxObject' ||
      ndType !== 'secApiNode' ||
      ndType !== 'fileSave' ||
      ndType !== 'scripting';

    if (nd.type === 'fluxBox' || nd.type === 'libraryNode') {
      if (combinedTextsKeys?.includes(nd?.id)) {
        const keys = Object.keys(combinedData?.[nd.id]);

        const newObject: {
          [key: string]: any;
        } = {};

        const textData = nd.data?.textData ? { ...nd.data.textData } : {};
        const fileLinkData = nd.data?.fileLinkData
          ? { ...nd.data.fileLinkData }
          : {};

        keys.forEach((key: string) => {
          if (combinedData?.[nd.id]?.[key]) {
            if (key.endsWith('Text')) {
              const varInputNodeId = key.replace('Text', '');
              if (
                textData[varInputNodeId] &&
                Array.isArray(textData[varInputNodeId])
              ) {
                const sourceData = combinedData?.[nd.id]?.[key];
                if (sourceData?.id) {
                  const itemIndex = textData[varInputNodeId].findIndex(
                    (item: any) => item.id === sourceData.id,
                  );
                  if (itemIndex >= 0) {
                    textData[varInputNodeId][itemIndex] = {
                      ...textData[varInputNodeId][itemIndex],
                      text: sourceData.text,
                    };
                  }
                }
              }
            }

            if (key.endsWith('FileLink')) {
              const varInputNodeId = key.replace('FileLink', '');
              if (
                fileLinkData[varInputNodeId] &&
                Array.isArray(fileLinkData[varInputNodeId])
              ) {
                const sourceData = combinedData?.[nd.id]?.[key];
                if (sourceData?.id) {
                  const itemIndex = fileLinkData[varInputNodeId].findIndex(
                    (item: any) => item.id === sourceData.id,
                  );
                  if (itemIndex >= 0) {
                    fileLinkData[varInputNodeId][itemIndex] = {
                      ...fileLinkData[varInputNodeId][itemIndex],
                      text: sourceData.text,
                    };
                  }
                }
              }
            }
          }
        });

        newObject.textData = textData;
        newObject.fileLinkData = fileLinkData;

        const ndTypeAsKeyOfAllNodesTargetBasedOnType =
          allNodesTargetBasedOnType[
            nd.type as keyof typeof allNodesTargetBasedOnType
          ];

        if (ndTypeAsKeyOfAllNodesTargetBasedOnType) {
          const targetKeys = Object.keys(combinedData?.[nd.id] || {});
          const keyMapping = ndTypeAsKeyOfAllNodesTargetBasedOnType as {
            [key: string]: string;
          };

          targetKeys.forEach((key: string) => {
            const mappedKey = keyMapping[key];
            if (
              mappedKey &&
              !key.endsWith('Text') &&
              !key.endsWith('FileLink')
            ) {
              const findData: { id: string; text: string }[] =
                nd?.data?.[mappedKey + 'Data'] || [];
              const sourceData = combinedData?.[nd.id]?.[key];

              if (findData?.length && sourceData?.id) {
                const mappedData = findData.map(item => {
                  if (sourceData.id === item?.id) {
                    return {
                      ...item,
                      text: sourceData.text,
                    };
                  }
                  return item;
                });
                newObject[mappedKey + 'Data'] = mappedData;
              }
            }
          });
        }

        return {
          ...nd,
          data: {
            ...nd.data,
            ...newObject,
          },
        };
      }
    }
    if (nd.type === 'mathFunction') {
      if (combinedTextsKeys?.includes(nd?.id)) {
        const newObject: {
          [key: string]: any;
        } = {};

        const targetKeys = Object.keys(combinedData?.[nd.id]);

        targetKeys.forEach((key: string) => {
          if (key === 'formula') {
            const findFormulaData: {
              id: string;
              text: string;
            }[] = nd.data['formulaData'] || [];

            if (findFormulaData?.length) {
              const mappedData = findFormulaData.map((item, index: number) => {
                if (combinedData?.[nd.id]?.[key]?.id === item?.id) {
                  return {
                    ...item,
                    text: combinedData?.[nd.id]?.[key]?.text,
                  };
                }
                return item;
              });

              newObject[key + 'Data'] = mappedData;
            }
          }
        });

        return {
          ...nd,
          data: {
            ...nd.data,
            ...newObject,
            variables: nd.data?.variables?.length
              ? nd.data.variables?.map((item: any, index: number) => {
                  const targetKeys = Object.keys(combinedData?.[nd.id]);

                  if (targetKeys?.includes(item?.id)) {
                    return {
                      ...item,
                      value: combinedData?.[nd.id]?.[item.id]?.text,
                    };
                  }

                  return item;
                })
              : [],
          },
        };
      }
    }

    if (nd.type === 'scripting') {
      if (combinedTextsKeys?.includes(nd?.id)) {
        const newObject: {
          [key: string]: any;
        } = {};

        const targetKeys = Object.keys(combinedData?.[nd.id]);

        targetKeys.forEach((key: string) => {
          if (key === 'script') {
            const findFormulaData: {
              id: string;
              text: string;
            }[] = nd.data['scriptData'] || [];

            if (findFormulaData?.length) {
              const mappedData = findFormulaData.map((item, index: number) => {
                if (combinedData?.[nd.id]?.[key]?.id === item?.id) {
                  return {
                    ...item,
                    text: combinedData?.[nd.id]?.[key]?.text,
                  };
                }
                return item;
              });

              newObject[key + 'Data'] = mappedData;
            }
          }
        });

        return {
          ...nd,
          data: {
            ...nd.data,
            ...newObject,
            variables: nd.data?.variables?.length
              ? nd.data.variables?.map((item: any, index: number) => {
                  const targetKeys = Object.keys(combinedData?.[nd.id]);

                  if (targetKeys?.includes(item?.id)) {
                    return {
                      ...item,
                      value: combinedData?.[nd.id]?.[item.id]?.text,
                    };
                  }

                  return item;
                })
              : [],
          },
        };
      }
    }

    if (nd.type === 'fileSave') {
      if (combinedTextsKeys?.includes(nd?.id)) {
        const newObject: {
          [key: string]: any;
        } = {};

        let files = nd.data?.files || [];

        const newFilesMap = files.map((file: any) => {
          const targetKeys = Object.keys(combinedData?.[nd.id]);

          const newMappedKeys = targetKeys.map((key: string) => {
            if (key.includes('-name') || key?.includes('-file')) {
              return file.id;
            }
          });

          if (newMappedKeys.includes(file.id)) {
            const name =
              combinedData?.[nd.id]?.[file.id + '-name']?.text ||
              file?.fileName;
            const fileName =
              combinedData?.[nd.id]?.[file.id + '-file']?.text || file?.value;

            return {
              ...file,
              value: name,
              fileName: fileName,
            };
          }

          return file;
        });

        return {
          ...nd,
          data: {
            ...nd.data,
            ...newObject,
            files: nd.data?.files?.length ? newFilesMap : [],
          },
        };
      }
    }

    if (nd.type === 'fluxObject') {
      if (combinedTextsKeys?.includes(nd?.id)) {
        if (changeType === 'clear-entries') {
          return nd;
        }

        if (changeType === 'execute') {
          const connectingEdge = edges.find(
            edge =>
              edge.source === id &&
              edge.target === nd.id &&
              edge.targetHandle === 'inputAdd',
          );

          if (!connectingEdge) {
            return nd;
          }

          const sourceNode = nds.find(n => n.id === id);

          const entryContent = combinedData?.[nd.id]?.['inputAdd']?.text;
          const isEmptyEntry = entryContent?.includes('\n')
            ? entryContent?.replace(/\n/g, '')?.trim() === ''
            : false;

          const hasValidContent = entryContent && !isEmptyEntry;
          const hasFlowTrigger = sourceNode?.data?.flowTrigger;
          const hasObjectData =
            objectData && Object.keys(objectData).length > 0;

          if (!hasValidContent && (!hasFlowTrigger || !hasObjectData)) {
            return nd;
          }

          const userSessionsChat = nd?.data?.userSessionsChat || [];
          const selectedChat = nd?.data?.selectedChat || 'default';
          const isChatMode = nd?.data?.sessionEnabled;
          let entries = nd.data.entries || [];

          if (nd?.data?.chat) {
            return nd;
          }

          if (isChatMode) {
            const findSession = userSessionsChat?.find(
              (session: any) => session?.id === selectedChat,
            );

            let sessionEntries = findSession?.entries || [];

            const newEntry = {
              content: entryContent,
              timestamp: createSystemTimestamp(),
              role: 'assistant',
            };

            sessionEntries = [...sessionEntries, newEntry];

            let newUserSessionsChat;
            if (findSession?.id) {
              newUserSessionsChat = userSessionsChat.map((session: any) =>
                session.id === selectedChat
                  ? { ...session, entries: sessionEntries }
                  : session,
              );
            } else {
              newUserSessionsChat = [
                ...userSessionsChat,
                {
                  id: selectedChat,
                  name: selectedChat === 'default' ? 'Primary' : selectedChat,
                  entries: sessionEntries,
                },
              ];
            }

            return {
              ...nd,
              data: {
                ...nd.data,
                userSessionsChat: newUserSessionsChat,
                entries: sessionEntries,
              },
            };
          } else {
            entries.push({
              content: entryContent,
              timestamp: createSystemTimestamp(),
              role: 'assistant',
            });

            return {
              ...nd,
              data: {
                ...nd.data,
                entries,
                userSessionsChat: [
                  ...(userSessionsChat || []).filter(
                    (session: any) => session.id !== 'default',
                  ),
                  {
                    id: 'default',
                    name: 'Primary',
                    entries,
                  },
                ],
              },
            };
          }
        }

        return nd;
      }
    }

    if (nd.type === 'nodesBox') {
      if (combinedTextsKeys?.includes?.(nd?.id)) {
        const targetKeys = Object.keys(combinedData[nd.id]);

        const newObject: {
          [key: string]: any;
        } = {};

        targetKeys.forEach((key: string) => {
          if (key) {
            const value = combinedData[nd.id][key];
            if (Array.isArray(value)) {
              newObject[key] = value
                .map((item: { text?: string }) => item?.text || '')
                .filter(Boolean)
                .join('\n\n');
            } else {
              newObject[key] = value?.text;
            }
          }
        });

        return {
          ...nd,
          data: {
            ...nd.data,
            ...newObject,
          },
        };
      }
    }

    if (check) {
      if (
        combinedTextsKeys?.includes(nd?.id) &&
        ndTypeAsKeyOfAllNodesTargetBasedOnType
      ) {
        const targetKeys = Object?.keys(combinedData[nd.id]);

        const getValuesFrom = Object.keys(
          ndTypeAsKeyOfAllNodesTargetBasedOnType,
        );

        const getAlsoValues = Object?.values(
          ndTypeAsKeyOfAllNodesTargetBasedOnType,
        );

        const keyMapping: any = getValuesFrom?.reduce(
          (acc: any, key, index) => {
            acc[key] = getAlsoValues?.[index];
            return acc;
          },
          {},
        );

        const newObject: {
          [key: string]: any;
        } = {};

        targetKeys.forEach((key: string) => {
          const mappedKey = keyMapping[key];

          if (nd.type === 'thirdPartyIntegration' && key.startsWith('field_')) {
            const fieldName = key.slice(6);
            const raw = combinedData[nd.id][key];
            newObject[fieldName] = raw?.text ?? raw;
            newObject[fieldName + 'Data'] = Array.isArray(raw)
              ? raw
              : raw != null
                ? [raw]
                : [];
            return;
          }
          if (mappedKey) {
            newObject[mappedKey + 'Data'] = combinedData[nd.id][key];
          }
        });

        const commandTextNodeValues = Object.values(
          ndTypeAsKeyOfAllNodesTargetBasedOnType,
        );

        const settingsData = {
          modelSettings: {},
        };

        if (targetKeys.includes('modelSettings')) {
          settingsData.modelSettings = combinedData[nd.id]['modelSettings'];
        }

        if (targetKeys.includes('modelVoiceSettings')) {
          settingsData.modelSettings =
            combinedData[nd.id]['modelVoiceSettings'];
        }

        // commandTextNodeValues.forEach((value: any) => {
        //   if (!newObject[value + 'disabled']) {
        //     newObject[value] = nd.data[value]?.includes('undefined')
        //       ? ''
        //       : nd.data[value];
        //     newObject[value + 'disabled'] = false;
        //   }
        // });

        if (nd?.type === 'arrayNode' && targetKeys.includes('content')) {
          const contentRaw = newObject?.['contentData'];
          const contentText = Array.isArray(contentRaw)
            ? contentRaw[0]?.text
            : contentRaw?.text;
          const formData = {
            options: 'Paragraph',
            processorArray: [],
            customOperator: '',
            startingPoint: 1,
            endingPoint: 5,
            completed: 1,
            executingArray: 1,
            executing: 'default',
            auto: false,
            breakWords: '',
            preInput: '',
            postInput: '',
            processorArrayItemLength: 1,
            ...nd?.data,
            content: contentText,
          };

          const processorArray = processorArrayFc(formData);

          newObject['processorArray'] = processorArray;
        }

        if (nd.type === 'secApiNode') {
          if (newObject['queryData']) {
            newObject['url'] = newObject['queryData'];
          }
        }

        const newObjectKeys = Object.keys(newObject);

        let newData: any = {};

        newObjectKeys.forEach((key: string) => {
          if (key) {
            if (nd.type === 'thirdPartyIntegration' && !key.endsWith('Data')) {
              newData[key] = newObject[key];
              return;
            }
            if (nd.type === 'thirdPartyIntegration' && key.endsWith('Data')) {
              newData[key] = newObject[key] ?? [];
              if (
                key === 'blobConfigData' &&
                newObject[key]?.text !== undefined
              ) {
                newData['blobConfig'] = newObject[key].text;
              }
              return;
            }
            const findData = nd?.data?.[key];

            if (!newData[key]) {
              newData[key] = findData;
            }

            if (
              key.includes(
                nodePassOnlyText[ndType as keyof typeof nodePassOnlyText],
              )
            ) {
              const rawPass = newObject[key];
              const passPiece = Array.isArray(rawPass) ? rawPass[0] : rawPass;
              newData[key.replace('Data', '')] = passPiece?.text;
              return;
            }

            if (key === 'processorArray') {
              newData[key] = newObject[key];
              return;
            }

            if (findData?.length) {
              const rawIncoming = newObject[key];
              const payloads: { id?: string; text?: string }[] = Array.isArray(
                rawIncoming,
              )
                ? rawIncoming
                : rawIncoming && typeof rawIncoming === 'object'
                  ? [rawIncoming]
                  : [];

              if (payloads.length === 0) {
                newData[key] = findData;
              } else {
                let mappedData = findData.map(
                  (item: { id: string; text: string }) => ({ ...item }),
                );
                for (const piece of payloads) {
                  mappedData = mappedData.map(
                    (item: { id: string; text: string }) =>
                      piece?.id === item?.id
                        ? { ...item, text: piece?.text ?? item.text }
                        : item,
                  );
                }
                newData[key] = mappedData;
              }
            }
          }
        });

        if (
          onlyInputPreview?.[ndType as keyof typeof onlyInputPreview] &&
          combinedData?.[nd.id]?.inputPreview?.text
        ) {
          newData['inputPreview'] = combinedData[nd.id]?.inputPreview?.text;
        }

        return {
          ...nd,
          data: {
            ...nd.data,
            ...newData,
            ...settingsData?.modelSettings,
          },
        };
      }
    }
    return nd;
  }));
};

export const onlyInputPreview = {
  imageReaderNode: 'inputPreview',
  audioReaderNode: 'inputPreview',
  documentSummarizer: 'inputPreview',
  imageGenerator: 'inputPreview',
};

export const nodePassOnlyText = {
  imageReaderNode: 'inputPreview',
  audioReaderNode: 'inputPreview',
  documentSummarizer: 'inputPreview',
  arrayNode: 'content',
  imageGenerator: 'inputPreview',
  mathFunction: 'varId',
  fluxBox: 'varId',
  libraryNode: 'varId',
  fileSave: 'varId',
  outputController: 'customFileName',
  thirdPartyIntegration: 'blobConfig',
};

export const nodePassOnlyTextRealHandleId = {
  imageReaderNode: 'inputPreview',
  audioReaderNode: 'inputPreview',
  documentSummarizer: 'inputPreview',
  arrayNode: 'content',
  imageGenerator: 'inputPreview',
  mathFunction: 'varId',
  fluxBox: 'varId',
  libraryNode: 'varId',
  fileSave: 'varId',
  thirdPartyIntegration: 'blobConfig',
};

export {
  changeLabel,
  connect,
  deleteEdgesData,
  handleChanges,
  updateEdgeData,
  updateNodeData,
};
